import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { motion } from 'framer-motion';
import type { MotionStyle } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode, MouseEvent } from 'react';

import { cn } from '@/lib/utils';

// Simplified vs. the coach portal's 5-variant set — a "simple" client-facing
// app has exactly one CTA color everywhere, so `primary` *is* the brand
// color directly. Built on shadcn's `cva` convention, themed to this app's
// CSS-variable tokens (bg-primary/bg-card/etc., src/styles/index.css).
// Full pill (`rounded-full`) — a calm, soft-touch signal that fits this
// app's meditation-adjacent tone, reversing DEC-0041's explicit "not a
// pill" call (DEC-0060).
export const buttonVariants = cva(
  'relative inline-flex items-center justify-center font-medium transition-all duration-200 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90 ring-1 ring-foreground/10',
        secondary: 'bg-card text-foreground border border-border hover:bg-secondary',
        ghost: 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-secondary',
        danger: 'bg-destructive/10 text-destructive hover:bg-destructive/15',
      },
      size: {
        sm: 'text-[12px] px-3 py-1.5 gap-1.5',
        md: 'text-[14px] px-4 py-2.5 gap-2',
        lg: 'text-[15px] px-5 py-3 gap-2',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

// framer-motion redefines these handlers with animation-specific signatures,
// which conflict with the standard DOM event handler types from ButtonHTMLAttributes.
type MotionConflictingHandlers =
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd';

export interface ButtonProps
  extends
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, MotionConflictingHandlers>,
    VariantProps<typeof buttonVariants> {
  /** Show a spinner and disable interaction. */
  loading?: boolean;
  /** Stretch to the full width of the container. */
  block?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

// forwardRef so Radix `asChild` triggers (AlertDialogTrigger, etc.) can
// attach their ref to the real DOM button — without this, Radix silently
// loses the trigger ref (React warns "Function components cannot be given
// refs"), which breaks its focus-return-to-trigger behavior on close.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    block = false,
    leadingIcon,
    trailingIcon,
    disabled,
    children,
    className,
    type = 'button',
    onClick,
    style,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled === true || loading;

  return (
    <motion.button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), block && 'w-full', className)}
      disabled={isDisabled}
      aria-busy={loading}
      whileHover={isDisabled ? {} : { scale: 1.02 }}
      whileTap={isDisabled ? {} : { scale: 0.98 }}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...(style !== undefined ? { style: style as MotionStyle } : {})}
      {...rest}
    >
      {loading && <Loader2 className="animate-spin w-4 h-4" />}
      {!loading && leadingIcon}
      {children}
      {!loading && trailingIcon}
    </motion.button>
  );
});
